import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  SideBarDef,
  ValueFormatterParams,
} from 'ag-grid-enterprise';
import { agGridTheme } from '@core/components/table/grid/config/grid-theme';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import {
  ReportingMetadata,
  ReportingRow,
} from 'demand-forecasting/reporting/types/reporting.types';
import { ReportingInitResponse } from '@api-response-types/reporting-ssrm.response';
import { ReportingStateService } from 'demand-forecasting/reporting/services/reporting-state.service';
import {
  formatValueAsInteger,
  formatValueAsDate,
} from '@core/utils/formatter-utility';
import {
  COLUMNS_TOOL_PANEL_ID,
  filtersToolPanelDef,
} from '@components/table/grid/config/columns-tool-panel';
import { MONTH_ORDER } from '@libs/shared/helpers/month.constants';
import { PIVOT_RESULT_FIELD_SEPARATOR } from '@libs/shared/helpers/pivot.constants';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

const REPORTING_GRID_STATE_QUERY_KEY = 'reportingGridState';

@Component({
  selector: 'pm-reporting-grid',
  templateUrl: './reporting-grid.component.html',
  styleUrl: './reporting-grid.component.scss',
  encapsulation: ViewEncapsulation.None,
  host: { class: 'flex flex-col flex-1 min-h-0' },
  imports: [AgGridAngular],
})
export class ReportingGridComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly reportingState = inject(ReportingStateService);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);
  private gridApi!: GridApi<ReportingRow>;

  private readonly stateChanged$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private isApplyingState = true;
  private urlGridState: {
    f?: Record<string, unknown>;
    p?: string[];
    g?: string[];
    v?: string[];
  } | null = null;
  // Inputs
  reportingMeta = input.required<ReportingInitResponse>();
  metadata = input.required<ReportingMetadata>();

  // Base column defs without URL state applied (used for reset)
  private readonly baseColumnDefs = computed(() => {
    const meta = this.metadata();
    if (!meta) return [];
    return this.buildColumnDefs(meta);
  });

  // SSRM Datasource
  protected readonly serverSideDatasource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
      this.reportingState
        .queryRows({
          startRow: params.request.startRow ?? 0,
          endRow: params.request.endRow ?? 100,
          rowGroupCols: params.request.rowGroupCols,
          valueCols: params.request.valueCols,
          pivotCols: params.request.pivotCols,
          pivotMode: params.request.pivotMode,
          groupKeys: params.request.groupKeys,
          filterModel:
            (params.request.filterModel as Record<string, unknown>) ?? null,
          sortModel: params.request.sortModel,
        })
        .then((response) => {
          params.success({
            rowData: response.rowData,
            rowCount: response.rowCount,
            pivotResultFields: response.pivotResultFields,
          });
        })
        .catch(() => params.fail());
    },
  };

  // Column defs with URL state applied
  protected readonly columnDefs = computed(() => {
    const baseCols = this.baseColumnDefs();
    if (baseCols.length === 0) return [];
    return this.applyUrlStateToColumnDefs(baseCols);
  });

  // Shared theme
  protected readonly theme = agGridTheme;

  protected readonly pivotResultFieldSeparator = PIVOT_RESULT_FIELD_SEPARATOR;

  // Auto group column (shown when row groups are active)
  protected readonly autoGroupColumnDef: ColDef<ReportingRow> = {
    cellRendererParams: {
      suppressCount: false,
    },
  };

  // Default column definition — valueFormatter applies to all columns
  // including dynamically generated pivot result columns
  protected readonly defaultColDef: ColDef<ReportingRow> = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    valueFormatter: this.numberFormatter,
  };

  // Sidebar configuration
  protected readonly sideBar: SideBarDef = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressRowGroups: false,
          suppressValues: false,
          suppressPivots: false,
          suppressPivotMode: false,
        },
      },
      filtersToolPanelDef(this.translateService),
    ],
    defaultToolPanel: COLUMNS_TOOL_PANEL_ID,
    position: 'right',
  };

  ngOnInit(): void {
    this.parseUrlState();

    this.stateChanged$
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.updateUrlFromGridState());

    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  // DYNAMIC COLUMN DEFINITION GENERATION
  // ═══════════════════════════════════════════════════════════════════

  private buildColumnDefs(meta: ReportingMetadata): ColDef<ReportingRow>[] {
    return [
      // 1. Dimension columns
      ...meta.dimensionColumns.map(
        (dim): ColDef<ReportingRow> => ({
          field: dim,
          headerName: dim,
          enableRowGroup: true,
          enablePivot: true,
          filter: 'agTextColumnFilter',
        }),
      ),
      // 2. Time columns (derived from date, pivotable)
      {
        field: 'year',
        headerName: 'Year',
        enablePivot: true,
        enableRowGroup: true,
        pivot: true,
        hide: true,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'quarter',
        headerName: 'Quarter',
        enablePivot: true,
        enableRowGroup: true,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'monthName',
        headerName: 'Month',
        enablePivot: true,
        enableRowGroup: true,
        filter: 'agTextColumnFilter',
        pivotComparator: this.monthPivotComparator,
      },
      {
        field: 'week',
        headerName: 'Week',
        enablePivot: true,
        enableRowGroup: true,
        filter: 'agNumberColumnFilter',
        pivotComparator: (a: string, b: string) => Number(a) - Number(b),
      },
      {
        field: meta.dateColumn,
        headerName: meta.dateColumn,
        enablePivot: false,
        enableRowGroup: true,
        filter: 'agDateColumnFilter',
      },
      // 3. Actuals, FC Version, and Forecast columns
      {
        headerName: 'Actuals',
        field: meta.targetColumn,
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['sum', 'avg', 'min', 'max', 'count'],
        valueFormatter: this.numberFormatter,
        cellStyle: { textAlign: 'right' },
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'fcVersion',
        headerName: 'FC Version',
        enableRowGroup: true,
        enablePivot: true,
        rowGroup: true,
        sort: 'desc',
        hide: true,
        filter: 'agTextColumnFilter',
        valueFormatter: (params) => {
          if (!params.value) return '-';
          const d = new Date(params.value);
          if (Number.isNaN(d.getTime())) return params.value;
          return `FC ${formatValueAsDate(d)}`;
        },
      },
      {
        field: meta.forecastColumn,
        headerName: 'Forecast',
        enableValue: true,
        aggFunc: 'sum',
        allowedAggFuncs: ['sum', 'avg', 'min', 'max', 'count'],
        valueFormatter: this.numberFormatter,
        cellStyle: { textAlign: 'right' },
        filter: 'agNumberColumnFilter',
      },
      // 4. Additional timeseries columns
      ...meta.additionalTimeseriesColumns.map(
        (ats): ColDef<ReportingRow> => ({
          headerName: ats,
          field: ats,
          enableValue: true,
          aggFunc: 'sum',
          allowedAggFuncs: ['sum', 'avg', 'min', 'max', 'count'],
          valueFormatter: this.numberFormatter,
          cellStyle: { textAlign: 'right' },
          filter: 'agNumberColumnFilter',
        }),
      ),
    ];
  }

  // ═══════════════════════════════════════════════════════════════════
  // GRID EVENT HANDLERS
  // ═══════════════════════════════════════════════════════════════════

  onGridReady(event: GridReadyEvent<ReportingRow>): void {
    this.gridApi = event.api;
  }

  onFirstDataRendered(): void {
    if (this.urlGridState?.f) {
      this.gridApi.setFilterModel(this.urlGridState.f);
    }
    setTimeout(() => {
      this.isApplyingState = false;
    });
  }

  onFilterChanged(): void {
    this.emitStateChangeIfNotApplying();
  }

  onColumnStateChanged(): void {
    this.emitStateChangeIfNotApplying();
  }

  private emitStateChangeIfNotApplying(): void {
    if (!this.isApplyingState) {
      this.stateChanged$.next();
    }
  }

  onPivotMaxColumnsExceeded(): void {
    this.toastrService.warning(
      this.translateService.instant(
        'REPORTING.GRID.PIVOT_MAX_COLUMNS_EXCEEDED',
      ),
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════════════════

  exportToCsv(): void {
    this.gridApi?.exportDataAsCsv({
      fileName: `reporting-export-${new Date().toISOString().split('T')[0]}.csv`,
    });
  }

  resetPivotConfig(): void {
    this.gridApi?.setGridOption('columnDefs', this.baseColumnDefs());
    this.gridApi?.setFilterModel({});
    this.stateChanged$.next();
  }

  expandAll(): void {
    this.gridApi?.expandAll();
  }

  collapseAll(): void {
    this.gridApi?.collapseAll();
  }

  // ═══════════════════════════════════════════════════════════════════
  // URL STATE SYNC
  // ═══════════════════════════════════════════════════════════════════

  private serializeGridState(): Record<string, unknown> | null {
    if (!this.gridApi) return null;

    const filterModel = this.gridApi.getFilterModel();
    const pivotCols = this.gridApi.getPivotColumns().map((c) => c.getColId());
    const groupCols = this.gridApi
      .getRowGroupColumns()
      .map((c) => c.getColId());
    const valueCols = this.gridApi.getValueColumns().map((c) => c.getColId());

    const hasFilter = Object.keys(filterModel).length > 0;
    const hasPivot = pivotCols.length > 0;
    const hasGroup = groupCols.length > 0;
    const hasValue = valueCols.length > 0;

    if (!hasFilter && !hasPivot && !hasGroup && !hasValue) return null;

    const state: Record<string, unknown> = {};
    if (hasFilter) state['f'] = filterModel;
    if (hasPivot) state['p'] = pivotCols;
    if (hasGroup) state['g'] = groupCols;
    if (hasValue) state['v'] = valueCols;
    return state;
  }

  private updateUrlFromGridState(): void {
    const state = this.serializeGridState();
    const queryParams: Record<string, string | null> = {};

    if (state) {
      const json = JSON.stringify(state);
      queryParams[REPORTING_GRID_STATE_QUERY_KEY] = btoa(json);
    } else {
      queryParams[REPORTING_GRID_STATE_QUERY_KEY] = null;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private parseUrlState(): void {
    const stateParam = this.route.snapshot.queryParamMap.get(
      REPORTING_GRID_STATE_QUERY_KEY,
    );
    if (!stateParam) return;
    try {
      this.urlGridState = JSON.parse(atob(stateParam));
    } catch (e) {
      console.warn('Failed to parse grid state from URL:', e);
    }
  }

  private applyUrlStateToColumnDefs(
    cols: ColDef<ReportingRow>[],
  ): ColDef<ReportingRow>[] {
    if (!this.urlGridState) return cols;

    const state = this.urlGridState;
    const pivotSet = state.p ? new Set(state.p) : null;
    const groupSet = state.g ? new Set(state.g) : null;
    const valueSet = state.v ? new Set(state.v) : null;

    if (!pivotSet && !groupSet && !valueSet) return cols;

    return cols.map((col) =>
      this.applyUrlStateToColDef(col, pivotSet, groupSet, valueSet),
    );
  }

  private applyUrlStateToColDef(
    col: ColDef<ReportingRow>,
    pivotSet: Set<string> | null,
    groupSet: Set<string> | null,
    valueSet: Set<string> | null,
  ): ColDef<ReportingRow> {
    const id = col.colId ?? (col.field as string);
    if (!id) return col;

    const modified = { ...col };

    if (pivotSet) {
      const isPivoted = pivotSet.has(id);
      modified.pivot = isPivoted;
      modified.hide = isPivoted || (col.pivot ? false : !!col.hide);
    }

    if (groupSet) {
      const isGrouped = groupSet.has(id);
      modified.rowGroup = isGrouped;
      if (col.enableRowGroup) {
        modified.hide = isGrouped;
      }
    }

    if (valueSet && col.enableValue) {
      modified.aggFunc = valueSet.has(id) ? (col.aggFunc ?? 'sum') : undefined;
    }

    return modified;
  }

  // ═══════════════════════════════════════════════════════════════════
  // FORMATTERS
  // ═══════════════════════════════════════════════════════════════════

  private numberFormatter(params: ValueFormatterParams): string {
    if (params.value == null) return '-';
    if (typeof params.value !== 'number') return params.value;
    return formatValueAsInteger(Math.round(params.value)) || '-';
  }

  private monthPivotComparator(a: string, b: string): number {
    return (MONTH_ORDER[a] ?? 0) - (MONTH_ORDER[b] ?? 0);
  }
}
